import React, { FC, memo, useEffect, useMemo } from 'react';
import cls from './Table.module.scss';

import PageDataLoader from 'src/components/UI/Loaders/PageDataLoader/PageDataLoader';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import selectors from 'src/redux/selectors';
import { TableVirtuoso } from 'react-virtuoso';
import TableHeader from './components/TableHeader';
import TrRender from './components/TrRender';
import useSearchQueryParams from 'src/hooks/useSearchQueryParams';
import { useTranslation } from 'react-i18next';
import TableRow from './components/TableRow';
import { useAppDispatch } from 'src/redux';
import { setTableDataIds } from 'src/redux/slices/selectedRows';
import { SortFieldTypes, TableProps } from './types';

export const baseRowHeight = 56;
export const defaultTableName = 'main';
const errorListener = (e: ErrorEvent) => {
  if (
    e.message ===
    'ResizeObserver loop completed with undelivered notifications.'
  ) {
    const resizeObserverErrDiv = document.getElementById(
      'webpack-dev-server-client-overlay-div'
    );
    const resizeObserverErr = document.getElementById(
      'webpack-dev-server-client-overlay'
    );
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute('style', 'display: none');
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute('style', 'display: none');
    }
  }
};
const Table: FC<TableProps> = ({
  config,
  tableData,
  filterOptions,
  asyncFilters,
  className = '',
  disableNoWrap,
  tableRef,
  dataLoading,
  rowHeight,
  actionsCount,
  virtuosoOverflow,
  customDeleteIcon,
  customEditIcon,
  actionStyles = '',
}) => {
  if (!config.tableName) {
    config.tableName = 'main';
  }
  const dispatch = useAppDispatch();
  const { params } = useSearchQueryParams({});
  const { t: tg } = useTranslation();
  const sortFields = useTypedSelector(selectors.getSortFields);
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.addEventListener('error', errorListener);
    }
    return () => {
      if (process.env.NODE_ENV === 'development') {
        window.removeEventListener('error', errorListener);
      }
    };
  }, []);

  const tableDataSorted = useMemo(() => {
    const sortField = sortFields?.[config.tableName || defaultTableName];
    if (!config.hasSort || !sortField) return tableData;
    const result = [...tableData].sort((a, b) => {
      const aData = a?.elements[sortField.columnId]?.sortValue;
      const bData = b?.elements[sortField.columnId]?.sortValue;
      let res = 0;
      if (!aData) {
        return 1;
      }
      if (!bData) {
        return -1;
      }
      if (aData < bData) {
        res = -1;
      } else if (aData > bData) {
        res = 1;
      }
      return sortField.type === SortFieldTypes.ASC ? res : res * -1;
    });
    return result;
  }, [tableData, config.hasSort, sortFields]);

  useEffect(() => {
    dispatch(setTableDataIds(tableDataSorted.map((item) => item.data.id)));
  }, [tableDataSorted]);

  const keyRowHeightRecalc =
    (rowHeight ? rowHeight.toString() : '0') + (disableNoWrap ? '0' : '1');
  const tableComponents = useMemo(() => {
    return {
      TableRow: (props: any) => (
        <TrRender
          props={props}
          rowHeight={rowHeight}
          tableDataSorted={tableDataSorted}
        />
      ),
    };
  }, [tableDataSorted]);
  const actionsNumber = useMemo(() => {
    if (actionsCount) return actionsCount;
    const hasEdit = tableData.find((item) => item.data.onEdit);
    const hasDelete = tableData.find((item) => item.data.onDelete);
    return 0 + (hasEdit ? 1 : 0) + (hasDelete ? 1 : 0);
  }, [tableData]);

  return (
    <div
      className={`${cls['table-wrapper']} ${className} ${cls['table']} ${
        disableNoWrap && cls['table-disable-nowrap']
      }`}
      ref={tableRef}
    >
      <TableVirtuoso
        key={keyRowHeightRecalc}
        totalCount={
          dataLoading || !tableData?.length ? 1 : tableDataSorted.length
        }
        increaseViewportBy={1000}
        style={{ ...(virtuosoOverflow ? { overflow: virtuosoOverflow } : {}) }}
        fixedItemHeight={56}
        // {...(disableNoWrap || rowHeight === 'disabled'
        //   ? {}
        //   : { fixedItemHeight: rowHeight || baseRowHeight })}
        // {...(disableNoWrap || rowHeight === 'disabled'
        //   ? {}
        //   : { defaultItemHeight: rowHeight || baseRowHeight })}
        fixedHeaderContent={() => {
          return (
            <TableHeader
              config={config}
              tableData={tableData}
              filterOptions={filterOptions}
              asyncFilters={asyncFilters}
              actionsNumber={actionsNumber}
            />
          );
        }}
        components={tableComponents}
        itemContent={(index) => {
          if (dataLoading) {
            return (
              <td colSpan={config.th.length}>
                <PageDataLoader />
              </td>
            );
          } else if (!tableData.length) {
            return (
              <td colSpan={config.th.length + (config.hasSelection ? 1 : 0)}>
                {Object.values(params || {}).length
                  ? tg('table-no-data-filter')
                  : tg('table-no-data-new')}
              </td>
            );
          } else {
            const tableDataItem = tableDataSorted[index];
            return (
              <TableRow
                config={config}
                tableDataItem={tableDataItem}
                hasActions={!!actionsNumber}
                customDeleteIcon={customDeleteIcon}
                customEditIcon={customEditIcon}
                actionStyles={actionStyles}
              />
            );
          }
        }}
      />
    </div>
  );
};
export default memo(Table);
